import React, { useEffect } from "react";
import { WizardStep } from "../../../core/components/wizard/WizardStep";
import ProgrammeDetails from "./steps/ProgrammeDetails";
import PathwayStep from "./steps/Pathway";
import { useProgramme } from "../contexts/programme.context";
import { Box, Container } from "@mui/material";
import { useOrganisation } from "../../../hooks/useOrganisation";
import { useAuth } from "../../../contexts/auth.context";
import { ProgrammeFacilitators } from "./steps/ProgrammeFacilitators";
import { StepperWizard } from "../../../core/components/wizard/StepperWizard";
import { useParams } from "react-router-dom";
import { useCollection } from "../../../hooks/useCollection";
import { useMobile } from "../../../hooks/useMobile";
import { isSoloPersonalOrganisation } from "../../../utils/courseDemo";
import { getRoleDisplayName } from "../../users/entities/RoleType";

export const CreateProgrammeWizard: React.FC = (): JSX.Element => {
    const { paramId } = useParams<{ paramId?: string }>();
    const { activeStep, completedSteps, changeStep, clearProvider, submitStep, programme } = useProgramme();
    const { user } = useAuth();
    const { organisation, getOrganisation } = useOrganisation();
    const { collection, getCollection } = useCollection();
    const { isMobile } = useMobile();
    // Prefer the fetched org; fall back to the session org so the Facilitator step
    // does not flash before by-user returns (still Demo, or already Full + licenceEndDate).
    const isSoloPersonalOrg = isSoloPersonalOrganisation(organisation ?? user?.Organisation, user?.isSchool);

    useEffect(() => {
        if (paramId && parseInt(paramId)) {
            getCollection(parseInt(paramId));
        }
    }, [paramId]);

    useEffect(() => {
        if (collection) {
            submitStep({ storyCollectionId: collection.id, ageRange: collection.ageRange });
            if (activeStep === 0) {
                changeStep(1);
            }
        }
    }, [collection]);

    useEffect(() => {
        if (user) {
            getOrganisation(user.id);
        }
        // Team orgs clear after the facilitator step. Individual skips that step, so leftover
        // programme.id would send the next Add Programme through the update path.
        if (completedSteps[2] || (isSoloPersonalOrg && programme?.id)) {
            clearProvider();
        }
    }, []);

    return (
        <Box p={4}>
            <Container maxWidth={"md"}>
                {isMobile && <div style={{ paddingTop: "64px" }} />}
                <StepperWizard activeStep={activeStep} goToStep={changeStep} orientation={"horizontal"}>
                    {[
                        <WizardStep
                            key="collection"
                            identifier={0}
                            heading={isMobile ? " " : "Collection"}
                            completed={completedSteps[0]}
                        >
                            <PathwayStep />
                        </WizardStep>,
                        <WizardStep
                            key="details"
                            identifier={1}
                            heading={isMobile ? " " : "Programme Details"}
                            completed={completedSteps[1]}
                        >
                            <ProgrammeDetails organisation={organisation} />
                        </WizardStep>,
                        ...(!isSoloPersonalOrg
                            ? [
                                  <WizardStep
                                      key="facilitator"
                                      identifier={2}
                                      heading={isMobile ? " " : getRoleDisplayName("Facilitator", user?.isSchool)}
                                      completed={completedSteps[2]}
                                  >
                                      <ProgrammeFacilitators />
                                  </WizardStep>,
                              ]
                            : []),
                    ]}
                </StepperWizard>
            </Container>
        </Box>
    );
};
